import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Table } from '../Table/Table';
import { Checkbox } from '../Checkbox/Checkbox';
import type { TableColumn } from '../Table/Table.types';
import type { MasterCrudTableProps, MasterCrudField, MasterCrudAction } from './MasterCrud.types';
import { EllipsisVerticalIcon, EnableCompanyIcon } from './icons';

/** Badge de estado por compañía (multicompañía): habilitado / no habilitado (prototipo N9). */
const CompanyStatusBadge: React.FC<{ enabled: boolean; enabledLabel: string; notEnabledLabel: string }> = ({ enabled, enabledLabel, notEnabledLabel }) => {
  if (enabled) {
    return (
      <span title={enabledLabel} className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#dbeefe] dark:bg-primary-900/20 text-primary-600 dark:text-primary-500 relative">
        <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" /><path d="M10 6h4" /><path d="M10 10h4" /></svg>
        <span className="absolute -bottom-0.5 -right-0.5 bg-white dark:bg-dark-bg-primary rounded-full leading-none p-px">
          <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
        </span>
      </span>
    );
  }
  return (
    <span title={notEnabledLabel} className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-dashed border-border-primary dark:border-dark-border-primary text-content-secondary">
      <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" /><path d="M10 6h4" /><path d="M10 10h4" /></svg>
    </span>
  );
};

// Estilo pixel-exact de los botones de acción de fila (borde azul de marca #93d1fd).
const ROW_ACTION_BTN =
  'inline-flex items-center justify-center w-7 h-7 bg-white dark:bg-dark-bg-primary border border-[#93d1fd] dark:border-primary-800 text-primary-600 dark:text-primary-500 rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-[#eff8ff] dark:hover:bg-primary-900/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed';

/** Chevron de orden (asc/desc/sin orden) del prototipo. */
const SortChevron: React.FC<{ state: 'asc' | 'desc' | null }> = ({ state }) => {
  if (state === 'asc') return (
    <svg className="w-3 h-3 text-primary-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
  );
  if (state === 'desc') return (
    <svg className="w-3 h-3 text-primary-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
  );
  return (
    <svg className="w-3 h-3 text-[#d4d4d8] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" /></svg>
  );
};

interface ColumnFilterHeaderProps {
  title: string;
  field: string;
  sortable: boolean;
  sortState: 'asc' | 'desc' | null;
  onSort: () => void;
  filterable: boolean;
  isEnum: boolean;
  enumOptions?: { value: string; label: string }[];
  value: string;
  onChange: (value: string, operator: 'contains' | 'equals') => void;
  t: (key: string, defaultValue?: string) => string;
}

/** Cabecera de columna con orden + embudo de filtro inline (prototipo L2/N4). */
const ColumnFilterHeader: React.FC<ColumnFilterHeaderProps> = ({
  title, sortable, sortState, onSort, filterable, isEnum, enumOptions = [], value, onChange, t,
}) => {
  const [open, setOpen] = useState(!!value);
  const [text, setText] = useState(value || '');
  const active = !!value;

  // Debounce 350ms para el input de texto (no dispara una petición por tecla).
  useEffect(() => {
    if (isEnum) return;
    const id = window.setTimeout(() => {
      if (text !== value) onChange(text, 'contains');
    }, 350);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const controlClasses = 'w-full min-w-0 h-[26px] box-border border border-border-primary dark:border-dark-border-primary rounded-[5px] px-1.5 text-[11px] text-content-primary dark:text-dark-content-primary bg-white dark:bg-dark-bg-primary outline-none focus:border-[#93d1fd] focus:ring-2 focus:ring-primary-500/10 transition-colors';

  return (
    <div className="flex flex-col gap-1 w-full">
      <div
        className={`flex items-center justify-between w-full gap-1 min-h-[28px] select-none ${sortable ? 'cursor-pointer' : ''}`}
        onClick={sortable ? onSort : undefined}
      >
        <span className="text-sm font-semibold truncate min-w-0 text-content-primary dark:text-dark-content-primary">{title}</span>
        <span className="flex items-center gap-0.5 flex-shrink-0">
          {filterable && (
            <button
              type="button"
              aria-expanded={open}
              title={open ? t('hideFilter', 'Ocultar filtro') : `${t('filter', 'Filtrar')} ${title}`}
              aria-label={open ? t('hideFilter', 'Ocultar filtro') : `${t('filter', 'Filtrar')} ${title}`}
              onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
              className={`inline-flex items-center justify-center p-0.5 rounded transition-colors hover:bg-[#eff8ff] dark:hover:bg-primary-900/20 ${
                open || active
                  ? 'text-primary-600 hover:text-primary-600'
                  : 'text-content-secondary hover:text-primary-600'
              }`}
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </button>
          )}
          {sortable && <SortChevron state={sortState} />}
        </span>
      </div>
      {filterable && open && (
        <div onClick={(e) => e.stopPropagation()}>
          {isEnum ? (
            <select
              className={`${controlClasses} cursor-pointer`}
              value={value}
              onChange={(e) => onChange(e.target.value, 'equals')}
            >
              <option value="">{t('all', 'Todos')}</option>
              {enumOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input
              type="text"
              size={1}
              autoComplete="off"
              placeholder={`${t('filter', 'Filtrar')}...`}
              className={controlClasses}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface RowActionsCellProps<T> {
  row: T;
  index: number;
  directActions: MasterCrudAction<T>[];
  menuActions: MasterCrudAction<T>[];
  rowActions?: (row: T, index: number) => React.ReactNode;
  t: (key: string, defaultValue?: string) => string;
}

/**
 * Celda de acciones de fila (prototipo N7): hasta 2 botones directos + kebab vertical.
 * El menú del kebab se renderiza en document.body (portal) y se posiciona con `fixed`
 * para que aparezca SOBRE la tabla (la celda sticky + overflow-auto lo recortarían).
 */
function RowActionsCell<T>({ row, index, directActions, menuActions, rowActions, t }: RowActionsCellProps<T>) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  const reposition = React.useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const mw = menu?.offsetWidth || 210;
    const mh = menu?.offsetHeight || 0;
    let top = r.bottom + 4;
    if (mh && top + mh > window.innerHeight - 8) top = r.top - mh - 4; // abrir hacia arriba si no cabe
    const left = Math.max(8, r.right - mw);
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    // scroll en cualquier contenedor (captura) cierra el menú
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const renderIcon = (action: MasterCrudAction<T>) => {
    if (action.id === 'link') return <EnableCompanyIcon />;
    const icon = typeof action.icon === 'function' ? action.icon(row) : action.icon;
    return <span className="w-[15px] h-[15px] inline-flex items-center justify-center">{icon}</span>;
  };

  return (
    <div className="flex items-center justify-center gap-3">
      {rowActions && rowActions(row, index)}

      {directActions.map((action) => {
        const name = typeof action.name === 'function' ? action.name(row) : action.name;
        const disabled = typeof action.disabled === 'function' ? action.disabled(row) : action.disabled;
        return (
          <button
            key={action.id || name}
            type="button"
            title={name}
            aria-label={name}
            disabled={disabled}
            onClick={() => action.action?.(row)}
            className={ROW_ACTION_BTN}
          >
            {renderIcon(action)}
          </button>
        );
      })}

      {menuActions.length > 0 && (
        <>
          <button
            ref={btnRef}
            type="button"
            title={t('moreActions', 'Más acciones')}
            aria-label={t('moreActions', 'Más acciones')}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className={ROW_ACTION_BTN}
          >
            <EllipsisVerticalIcon className="w-[15px] h-[15px]" />
          </button>
          {open && createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ position: 'fixed', top: pos.top, left: pos.left }}
              className="z-[60] min-w-[210px] bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-md shadow-md py-1"
            >
              {menuActions.map((action) => {
                const name = typeof action.name === 'function' ? action.name(row) : action.name;
                const icon = typeof action.icon === 'function' ? action.icon(row) : action.icon;
                const disabled = typeof action.disabled === 'function' ? action.disabled(row) : action.disabled;
                const isDelete = action.id === 'delete' || name.toLowerCase().includes('eliminar') || name.toLowerCase().includes('desactivar');
                return (
                  <button
                    key={action.id || name}
                    type="button"
                    role="menuitem"
                    disabled={disabled}
                    onClick={() => { action.action?.(row); setOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors disabled:opacity-40 ${isDelete ? 'text-error-content hover:bg-error/10' : 'text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'}`}
                  >
                    {icon && <span className="w-4 h-4 flex-shrink-0 inline-flex items-center justify-center">{icon}</span>}
                    <span>{name}</span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )}
        </>
      )}
    </div>
  );
}

/**
 * MasterCrudTable - Adaptador de tabla para MasterCrud
 */
export const MasterCrudTable = <T extends Record<string, any>>({
  fields,
  data,
  loading = false,
  emptyMessage,
  onSort,
  sortColumn,
  sortDirection,
  pagination,
  showRowActions = true,
  actions = [],
  rowActions,
  className = '',
  locale = 'es',
  enableMultiSelect = false,
  selectedIds = [],
  onSelectionChange,
  stickyHeader = false,
  columnFilterableFields,
  columnFilters = {},
  onColumnFilterChange,
  showCompanyStatusColumn = false,
  isRecordCompanyEnabled,
  onCompanyStatusClick,
  t,
}: MasterCrudTableProps<T>) => {
  const finalEmptyMessage = emptyMessage || t('empty', 'No hay registros disponibles');

  // Orden controlado por el padre (MasterCrud): ciclo sin orden → asc → desc.
  const handleColumnSort = (field: string) => {
    if (!onSort) return;
    const next = sortColumn !== field ? 'asc' : sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc';
    onSort(field, next);
  };

  // Opciones de enum para el filtro por columna (boolean / select).
  const enumOptionsFor = (field: MasterCrudField<T>): { value: string; label: string }[] | null => {
    if (field.type === 'boolean') {
      return [
        { value: 'true', label: field.config?.trueLabel || t('active', 'Activo') },
        { value: 'false', label: field.config?.falseLabel || t('inactive', 'Inactivo') },
      ];
    }
    if (field.type === 'select' && field.options?.length) {
      return field.options.map((o) => ({ value: String(o.value), label: o.label }));
    }
    return null;
  };

  // ===== CONVERTIR FIELDS A COLUMNS =====
  const columns: TableColumn<T>[] = fields
    .filter((field) => {
      if (field.config?.showInList !== undefined) return field.config.showInList;
      if (field.config?.visibleInTable !== undefined) return field.config.visibleInTable;
      return true;
    })
    .map((field) => {
      const accessorKey = field.accessorKey as string;
      const title = t(accessorKey, field.header);
      const sortable = field.config?.sortable ?? false;
      const filterable = !!columnFilterableFields?.includes(accessorKey) && !!onColumnFilterChange && field.type !== 'lookup';
      const enumOptions = enumOptionsFor(field);
      const useRawHeader = sortable || filterable;

      return {
        header: useRawHeader ? (
          <ColumnFilterHeader
            title={title}
            field={accessorKey}
            sortable={sortable}
            sortState={sortColumn === accessorKey ? (sortDirection ?? null) : null}
            onSort={() => handleColumnSort(accessorKey)}
            filterable={filterable}
            isEnum={!!enumOptions}
            enumOptions={enumOptions || undefined}
            value={columnFilters[accessorKey] ?? ''}
            onChange={(value, operator) => onColumnFilterChange?.(accessorKey, value, operator)}
            t={t}
          />
        ) : title,
        headerRaw: useRawHeader,
        accessor: field.accessorKey as keyof T,
        sortable,
        align: field.config?.align ?? 'left',
        width: field.config?.columnWidth,
        render: field.renderCell
          ? (value: any, row: T, index: number) => field.renderCell!(value, row, index)
          : (value: any) => renderCellByType(value, field),
      };
    });

  // ===== AGREGAR COLUMNA DE SELECCIÓN =====
  if (enableMultiSelect) {
    columns.unshift({
      header: (
        <Checkbox
          checked={data.length > 0 && selectedIds.length === data.length}
          onChange={(e) => {
            if (e.target.checked) {
              onSelectionChange?.(data.map((r: any) => r.id || r.Id || r.codigo));
            } else {
              onSelectionChange?.([]);
            }
          }}
          aria-label={t('selectAll', 'Seleccionar todos')}
        />
      ),
      accessor: (() => null) as any,
      align: 'center',
      width: '100px', // Aumentado para visibilidad completa
      fixedWidth: true,
      render: (_: any, row: T) => {
        const id = row.id || row.Id || row.codigo;
        const isSelected = selectedIds.includes(id);
        return (
          <div className="flex justify-center items-center">
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  onSelectionChange?.([...selectedIds, id]);
                } else {
                  onSelectionChange?.(selectedIds.filter(item => item !== id));
                }
              }}
              aria-label={t('selectRecord', 'Seleccionar registro')}
            />
          </div>
        );
      },
    });
  }

  // ===== COLUMNA DE ESTADO POR COMPAÑÍA (multicompañía, primera columna) =====
  if (showCompanyStatusColumn) {
    columns.unshift({
      header: (
        <span className="inline-flex items-center justify-center text-content-secondary" title={t('companyStatus', 'Habilitación en la compañía seleccionada')}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" /><path d="M10 6h4" /><path d="M10 10h4" /><path d="M10 14h4" /><path d="M10 18h4" /></svg>
        </span>
      ),
      accessor: (() => null) as any,
      align: 'center',
      width: '46px',
      fixedWidth: true,
      render: (_: any, row: T) => {
        const enabled = isRecordCompanyEnabled ? isRecordCompanyEnabled(row) : true;
        const badge = (
          <CompanyStatusBadge
            enabled={enabled}
            enabledLabel={t('enabledInCompany', 'Habilitado en la compañía seleccionada')}
            notEnabledLabel={t('notEnabledInCompany', 'No habilitado en la compañía seleccionada')}
          />
        );
        return onCompanyStatusClick ? (
          <button type="button" onClick={() => onCompanyStatusClick(row)} className="inline-flex">{badge}</button>
        ) : badge;
      },
    });
  }

  // ===== AGREGAR COLUMNA DE ACCIONES =====
  if (showRowActions) {
    columns.push({
      header: t('actions', 'Acciones'),
      accessor: (() => null) as any,
      align: 'center',
      stickyRight: true,
      // Ancho JUSTO para 3 botones con separación táctil (3×28px + 2×12px de gap = 108px
      // + 32px de padding de celda = 140px). `fixedWidth` evita que la columna se expanda:
      // nunca es más ancha de lo necesario.
      width: '140px',
      fixedWidth: true,
      render: (_: any, row: T, index: number) => {
        // Regla de acciones por registro (máx. 3 botones):
        // - Hasta 3 acciones visibles: se muestran TODAS de frente (sin kebab).
        // - 4 o más: 2 directas + botón "Más acciones" (kebab) completando 3; el resto
        //   (a partir de la 3ª) va dentro del desplegable del kebab.
        // Se filtra por visibilidad ANTES de repartir para no "gastar" un slot.
        const isVisible = (action: MasterCrudAction<T>) =>
          typeof action.visible === 'function' ? action.visible(row) : action.visible !== false;

        const visibleActions = actions.filter(isVisible);
        const MAX_DIRECT = 3;

        let directActions: MasterCrudAction<T>[];
        let menuActions: MasterCrudAction<T>[];
        if (visibleActions.length <= MAX_DIRECT) {
          directActions = visibleActions;
          menuActions = [];
        } else {
          // Prioriza Editar (edit) + Habilitar (link) como directas; si no existen, toma las 2 primeras.
          const priority = visibleActions.filter((a) => a.id && ['edit', 'link'].includes(a.id)).slice(0, 2);
          const fill = priority.length < 2
            ? visibleActions.filter((a) => !priority.includes(a)).slice(0, 2 - priority.length)
            : [];
          directActions = [...priority, ...fill];
          menuActions = visibleActions.filter((a) => !directActions.includes(a));
        }

        return (
          <RowActionsCell
            row={row}
            index={index}
            directActions={directActions}
            menuActions={menuActions}
            rowActions={rowActions}
            t={t}
          />
        );
      },
    });
  }

  const renderCellByType = (value: any, field: MasterCrudField<T>): React.ReactNode => {
    if (value === null || value === undefined) return <span className="text-content-tertiary dark:text-content-tertiary">—</span>;
    switch (field.type) {
      case 'boolean': {
        const trueLabel = field.config?.trueLabel || t('active', 'Activo');
        const falseLabel = field.config?.falseLabel || t('inactive', 'Inactivo');
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${value ? 'bg-[#ecfccb] text-[#4d7c0f] dark:bg-lime-900/30 dark:text-lime-400' : 'bg-background-secondary text-content-tertiary dark:bg-dark-bg-secondary dark:text-dark-content-tertiary'}`}>
            {value ? trueLabel : falseLabel}
          </span>
        );
      }
      case 'date': try { return new Date(value).toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch { return String(value); }
      case 'number': return <span className="tabular-nums">{typeof value === 'number' ? value.toLocaleString(locale) : value}</span>;
      case 'select': return field.options?.find((opt) => opt.value === value)?.label ?? String(value);
      case 'email': return <a href={`mailto:${value}`} className="text-primary-600 hover:underline dark:text-primary-600">{value}</a>;
      case 'lookup': if (field.lookupConfig && typeof value === 'object' && value !== null) { return field.lookupConfig.displayFields.map(k => (value as any)[k]).filter(v => v !== undefined && v !== null).join(' - ') || String(value); } return String(value);
      default: return String(value);
    }
  };

  const handleSort = (column: keyof T | string, direction: 'asc' | 'desc' | null) => { if (onSort) onSort(String(column), direction); };

  return (
    <Table
      columns={columns} data={data} loading={loading} emptyMessage={finalEmptyMessage}
      variant="basic" showBorder={false} showShadow={false} showPadding={false} fullWidth
      stickyHeader={stickyHeader} accentHover dense
      onSort={handleSort} sortColumn={sortColumn} sortDirection={sortDirection}
      pagination={pagination} className={`${className} !bg-transparent !shadow-none !rounded-none !p-0`}
      isRowSelected={(row) => {
        const id = row.id || row.Id || row.codigo;
        return selectedIds.includes(id);
      }}
    />
  );
};
